import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCompetenciasComDado } from '../../api/queries/chamados'
import {
  useChecklistDeRecebimento,
  useConformidadeDeGmud,
  useRegistroDeOcorrencias,
} from '../../api/queries/relatorios'
import { Abas } from '../../components/Abas'
import { AvisoDeErro, Carregando, Esqueleto } from '../../components/Estados'
import { SemCompetencia } from '../../components/SemCompetencia'
import { TEXTOS } from '../../lib/glossario'
import { SeletorDeCompetencia } from '../chamados/SeletorDeCompetencia'
import { ChecklistDeRecebimento } from './ChecklistDeRecebimento'
import { ConformidadeDeGmud } from './ConformidadeDeGmud'
import { RegistroDeOcorrencias } from './RegistroDeOcorrencias'

/** As três abas. A do registro NÃO tem competência: ele é do contrato inteiro. */
const ABAS = [
  { id: 'recebimentos', rotulo: 'Recebimento de relatórios' },
  { id: 'conformidade', rotulo: 'Conformidade das mudanças' },
  { id: 'ocorrencias', rotulo: 'Registro de ocorrências' },
] as const

/** A aba aberta quando o endereço não pede nenhuma. */
const ABA_PADRAO = 'recebimentos'

/**
 * Os documentos do contrato que a fiscalização produz.
 *
 * **A aba e a competência vivem na URL**, pelo mesmo motivo do fechamento: `/relatorios?aba=
 * conformidade&competencia=2026-09` é um endereço que vai por e-mail para quem vai conferir, e
 * recarregar a página não pode devolver a pessoa a outro documento.
 *
 * @returns a tela dos relatórios
 */
export function PaginaRelatorios() {
  const [parametros, definirParametros] = useSearchParams()
  const { data: competencias, isPending: carregando } = useCompetenciasComDado()

  const aba = parametros.get('aba') ?? ABA_PADRAO
  const escolhida = parametros.get('competencia') ?? ''
  const competencia = escolhida || competencias?.[0]?.competencia || ''

  const trocar = useCallback(
    (chave: string, valor: string) => {
      const proximos = new URLSearchParams(parametros)
      proximos.set(chave, valor)
      definirParametros(proximos)
    },
    [parametros, definirParametros],
  )

  return (
    <div className="space-y-5">
      <header>
        <h1 className="titulo-da-pagina">{TEXTOS.relatorios.titulo}</h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.relatorios.ajuda}</p>
      </header>

      {/* O seletor só aparece nas abas que são POR MÊS. O registro de ocorrências é contínuo — um
          seletor ali sugeriria que ele filtra, e a lista continuaria a mesma. */}
      {aba !== 'ocorrencias' && carregando && <Carregando />}
      {aba !== 'ocorrencias' && competencias && competencias.length > 0 && (
        <div className="cartao max-w-md p-4">
          <SeletorDeCompetencia
            competencias={competencias}
            valor={competencia}
            aoEscolher={(valor) => trocar('competencia', valor)}
          />
        </div>
      )}

      <Abas abas={[...ABAS]} ativa={aba} aoMudar={(id) => trocar('aba', id)} rotulo="Documentos do contrato">
        {aba === 'recebimentos' && <Recebimentos competencia={competencia} />}
        {aba === 'conformidade' && <Conformidade competencia={competencia} />}
        {aba === 'ocorrencias' && <Ocorrencias />}
      </Abas>
    </div>
  )
}

/**
 * O checklist de recebimento do mês.
 *
 * A guarda do mês vazio vem ANTES do `isPending`, e é a ordem que importa: sem competência a
 * consulta nasce `enabled: false`, e uma consulta desabilitada fica em `isPending` para sempre.
 * O `<Esqueleto>` então pulsava indefinidamente numa base sem chamado nenhum — que é a base com que
 * a UAT começa, e a "tela quebrada" do cenário 39.
 */
function Recebimentos({ competencia }: { competencia: string }) {
  const { data, isPending, isError, error } = useChecklistDeRecebimento(competencia)
  if (!competencia) {
    return <SemCompetencia />
  }
  if (isPending) {
    return <Esqueleto linhas={4} />
  }
  if (isError) {
    return <AvisoDeErro erro={error} />
  }
  return <ChecklistDeRecebimento checklist={data} />
}

/** A conformidade das mudanças do mês — mesma guarda do mês vazio, mesma razão. */
function Conformidade({ competencia }: { competencia: string }) {
  const { data, isPending, isError, error } = useConformidadeDeGmud(competencia)
  if (!competencia) {
    return <SemCompetencia />
  }
  if (isPending) {
    return <Esqueleto linhas={4} />
  }
  if (isError) {
    return <AvisoDeErro erro={error} />
  }
  return <ConformidadeDeGmud conformidade={data} />
}

/** O registro contínuo — do contrato inteiro, não do mês. */
function Ocorrencias() {
  const { data, isPending, isError, error } = useRegistroDeOcorrencias()
  if (isPending) {
    return <Esqueleto linhas={4} />
  }
  if (isError) {
    return <AvisoDeErro erro={error} />
  }
  return <RegistroDeOcorrencias registro={data} />
}
